<template>
  <Content class="dashboard">
    <Spin v-if="loading" fix></Spin>
    
    <!-- 关键指标卡片 -->
    <Row :gutter="16" class="metrics-row" v-if="dashboardData">
      <Col span="6" v-for="metric in metrics" :key="metric.key">
        <Card class="metric-card" :class="metric.trend">
          <div class="metric-content">
            <div class="metric-value">{{ formatValue(metric.value, metric.key) }}</div>
            <div class="metric-label">{{ metric.label }}</div>
            <div class="metric-trend" v-if="metric.growth !== undefined">
              <Icon 
                :type="metric.trend === 'up' ? 'ios-arrow-up' : metric.trend === 'down' ? 'ios-arrow-down' : 'ios-remove'" 
                :class="metric.trend"
              />
              <span :class="metric.trend">{{ metric.growth }}%</span>
            </div>
            <div class="metric-total" v-if="metric.total !== undefined">
              / {{ metric.total }}
            </div>
          </div>
        </Card>
      </Col>
    </Row>

    <!-- 图表区域 -->
    <Row :gutter="16" class="charts-row" v-if="dashboardData">
      <Col span="12">
        <Card title="订单趋势（最近7天）">
          <div ref="orderTrendChart" style="height: 300px;"></div>
        </Card>
      </Col>
      <Col span="12">
        <Card title="收入趋势（最近30天）">
          <div ref="revenueTrendChart" style="height: 300px;"></div>
        </Card>
      </Col>
    </Row>

    <Row :gutter="16" class="charts-row" v-if="dashboardData">
      <Col span="24">
        <Card title="订单状态分布">
          <div ref="orderStatusChart" style="height: 300px;"></div>
        </Card>
      </Col>
    </Row>
    <Alert type="info" show-icon style="margin-bottom: 16px;">
      退款操作请在「商品订单」中处理：确认/出餐、同意取消退款、主动取消退款。
    </Alert>
  </Content>
</template>

<script>
import { getDashboardData } from '@/api/home'
import Echarts from 'echarts'

export default {
  name: 'Welcome',
  data() {
    return {
      loading: true,
      dashboardData: null,
      orderTrendChart: null,
      revenueTrendChart: null,
      orderStatusChart: null
    }
  },
  computed: {
    metrics() {
      if (!this.dashboardData || !this.dashboardData.metrics) return []
      
      const m = this.dashboardData.metrics
      return [
        { 
          key: 'todayOrders', 
          label: m.todayOrders?.label || '今日订单', 
          value: m.todayOrders?.value || 0,
          growth: m.todayOrders?.growth,
          trend: m.todayOrders?.trend || 'stable'
        },
        { 
          key: 'todayRevenue', 
          label: m.todayRevenue?.label || '今日净营业额', 
          value: m.todayRevenue?.value || 0,
          growth: m.todayRevenue?.growth,
          trend: m.todayRevenue?.trend || 'stable'
        },
        { 
          key: 'pendingConfirm', 
          label: m.pendingConfirm?.label || '待确认订单', 
          value: m.pendingConfirm?.value || 0,
          trend: m.pendingConfirm?.trend || 'stable'
        },
        { 
          key: 'preparing', 
          label: m.preparing?.label || '制作中', 
          value: m.preparing?.value || 0,
          trend: m.preparing?.trend || 'stable'
        },
        { 
          key: 'activeUsers', 
          label: m.activeUsers?.label || '活跃用户', 
          value: m.activeUsers?.value || 0,
          total: m.activeUsers?.total,
          trend: m.activeUsers?.trend || 'stable'
        },
        { 
          key: 'pendingComplaints', 
          label: m.pendingComplaints?.label || '待处理投诉', 
          value: m.pendingComplaints?.value || 0,
          trend: m.pendingComplaints?.trend || 'stable'
        },
        { 
          key: 'lowStockGoods', 
          label: m.lowStockGoods?.label || '库存预警', 
          value: m.lowStockGoods?.value || 0,
          trend: m.lowStockGoods?.trend || 'stable'
        }
      ]
    }
  },
  mounted() {
    this.loadDashboardData()
  },
  beforeDestroy() {
    // 销毁图表实例
    if (this.orderTrendChart) {
      this.orderTrendChart.dispose()
    }
    if (this.revenueTrendChart) {
      this.revenueTrendChart.dispose()
    }
    if (this.orderStatusChart) {
      this.orderStatusChart.dispose()
    }
  },
  methods: {
    async loadDashboardData() {
      this.loading = true
      try {
        const res = await getDashboardData()
        if (res.data && (res.data.success === true || res.data.code === 200)) {
          this.dashboardData = res.data.data
          this.$nextTick(() => {
            this.initCharts()
          })
        } else {
          this.$Message.error(res.data?.message || res.data?.msg || '获取仪表盘数据失败')
        }
      } catch (error) {
        this.$Message.error('获取仪表盘数据失败')
        console.error(error)
      } finally {
        this.loading = false
      }
    },
    initCharts() {
      if (!this.dashboardData) return
      
      this.initOrderTrendChart()
      this.initRevenueTrendChart()
      this.initOrderStatusChart()
    },
    initOrderTrendChart() {
      if (!this.$refs.orderTrendChart) return
      
      this.orderTrendChart = Echarts.init(this.$refs.orderTrendChart)
      const orderTrend = this.dashboardData.orderTrend || []
      
      const option = {
        tooltip: {
          trigger: 'axis',
          axisPointer: {
            type: 'cross'
          }
        },
        legend: {
          data: ['订单数', '收入']
        },
        xAxis: {
          type: 'category',
          data: orderTrend.map(item => {
            const date = new Date(item.date)
            return `${date.getMonth() + 1}/${date.getDate()}`
          })
        },
        yAxis: [
          {
            type: 'value',
            name: '订单数',
            position: 'left'
          },
          {
            type: 'value',
            name: '收入(元)',
            position: 'right'
          }
        ],
        series: [
          {
            name: '订单数',
            type: 'bar',
            data: orderTrend.map(item => item.count || 0),
            itemStyle: {
              color: '#409EFF'
            }
          },
          {
            name: '收入',
            type: 'line',
            yAxisIndex: 1,
            data: orderTrend.map(item => (item.revenue || 0).toFixed(2)),
            itemStyle: {
              color: '#67C23A'
            }
          }
        ]
      }
      
      this.orderTrendChart.setOption(option)
    },
    initRevenueTrendChart() {
      if (!this.$refs.revenueTrendChart) return
      
      this.revenueTrendChart = Echarts.init(this.$refs.revenueTrendChart)
      const revenueTrend = this.dashboardData.revenueTrend || []
      
      // 按周聚合数据
      const weeklyData = {}
      revenueTrend.forEach(item => {
        const date = new Date(item._id)
        const week = this.getWeekOfYear(date)
        if (!weeklyData[week]) {
          weeklyData[week] = 0
        }
        weeklyData[week] += item.revenue || 0
      })
      
      const weeks = Object.keys(weeklyData).sort()
      const revenues = weeks.map(w => weeklyData[w].toFixed(2))
      
      const option = {
        tooltip: {
          trigger: 'axis'
        },
        xAxis: {
          type: 'category',
          data: weeks.map(w => `第${w}周`)
        },
        yAxis: {
          type: 'value',
          name: '收入(元)'
        },
        series: [
          {
            name: '收入',
            type: 'line',
            smooth: true,
            data: revenues,
            areaStyle: {
              color: {
                type: 'linear',
                x: 0,
                y: 0,
                x2: 0,
                y2: 1,
                colorStops: [
                  { offset: 0, color: 'rgba(103, 194, 58, 0.3)' },
                  { offset: 1, color: 'rgba(103, 194, 58, 0.1)' }
                ]
              }
            },
            itemStyle: {
              color: '#67C23A'
            }
          }
        ]
      }
      
      this.revenueTrendChart.setOption(option)
    },
    initOrderStatusChart() {
      if (!this.$refs.orderStatusChart) return
      
      this.orderStatusChart = Echarts.init(this.$refs.orderStatusChart)
      const statusStats = this.dashboardData.orderStatusStats || []
      
      const option = {
        tooltip: {
          trigger: 'item',
          formatter: '{a} <br/>{b}: {c} ({d}%)'
        },
        legend: {
          orient: 'vertical',
          left: 'left',
          data: statusStats.map(s => s.statusName)
        },
        series: [
          {
            name: '订单状态',
            type: 'pie',
            radius: ['40%', '70%'],
            avoidLabelOverlap: false,
            itemStyle: {
              borderRadius: 10,
              borderColor: '#fff',
              borderWidth: 2
            },
            label: {
              show: true,
              formatter: '{b}: {c}'
            },
            emphasis: {
              label: {
                show: true,
                fontSize: '16',
                fontWeight: 'bold'
              }
            },
            data: statusStats.map(s => ({
              value: s.count,
              name: s.statusName
            }))
          }
        ]
      }
      
      this.orderStatusChart.setOption(option)
    },
    formatValue(value, key) {
      if (key === 'todayRevenue') {
        return '¥' + parseFloat(value).toFixed(2)
      }
      return value
    },
    getWeekOfYear(date) {
      const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()))
      const dayNum = d.getUTCDay() || 7
      d.setUTCDate(d.getUTCDate() + 4 - dayNum)
      const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1))
      return Math.ceil((((d - yearStart) / 86400000) + 1) / 7)
    }
  }
}
</script>

<style lang="less" scoped>
.dashboard {
  .metrics-row {
    margin-bottom: 16px;
    
    .metric-card {
      text-align: center;
      transition: all 0.3s;
      
      &:hover {
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
        transform: translateY(-2px);
      }
      
      .metric-content {
        .metric-value {
          font-size: 32px;
          font-weight: bold;
          color: #2c3e50;
          margin-bottom: 8px;
        }
        
        .metric-label {
          font-size: 14px;
          color: #909399;
          margin-bottom: 8px;
        }
        
        .metric-trend {
          font-size: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 4px;
          
          &.up {
            color: #67C23A;
          }
          
          &.down {
            color: #F56C6C;
          }
          
          &.warning {
            color: #E6A23C;
          }
        }
        
        .metric-total {
          font-size: 12px;
          color: #909399;
          margin-top: 4px;
        }
      }
    }
  }
  
  .charts-row {
    margin-bottom: 16px;
  }
}
</style>
